import { flagCache, notifyObservers } from "./cache";
import { getConfigs } from "./configs";
import { getParamsForClient, getTargetingParams, setClientParams, setTargetingParams } from "./targeting";
import { stringifyAndCompare } from "./utils";
// univerial `btoa` function for node
function universalBtoa(inString) {
    if (typeof btoa !== "undefined") {
        return btoa(inString);
    }
    return Buffer.from(inString, "utf-8").toString("base64");
}
/**
 * Encodes a JSON object into a Base64 URL-safe string. This function first converts the JSON object
 * into a string using JSON.stringify, then encodes it to Base64 using a universal Base64 encoder,
 * and finally makes the Base64 string URL-safe by replacing certain characters according to the
 * Base64 URL encoding specifications.
 *
 * @param {Object} JSONParams - The JSON object containing the parameters to be encoded.
 * @returns {string} The URL-safe Base64 encoded string of the JSON parameters.
 *
 * @example
 * // Example of encoding targeting parameters
 * const params = { user: "user123", region: "us-west" };
 * const encodedParams = encodeTargetingParams(params);
 * console.log(encodedParams); // Outputs the URL-safe Base64 encoded string
 */ function encodeTargetingParams(JSONParams) {
    return universalBtoa(JSON.stringify(JSONParams)).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
}
function fetchWithTimeout(fetchProvider, url, options, timeout) {
    if (options === void 0) options = {};
    if (timeout === void 0) timeout = 1000;
    return new Promise(function(resolve, reject) {
        // Start the fetch request using the provided fetchProvider
        var fetchPromise = fetchProvider(url, options);
        // Set up the timeout
        var timeoutPromise = new Promise(function(_, timeoutReject) {
            setTimeout(function() {
                return timeoutReject(new Error("Request timed out"));
            }, timeout);
        });
        // Use Promise.race to race the fetch against the timeout
        Promise.race([
            fetchPromise,
            timeoutPromise
        ]).then(resolve).catch(reject);
    });
}
/**
 * Performs a fetch operation for LaunchDarkly flag evaluations using the specified client ID and encoded targeting parameters.
 * The fetch operation is configured to use a specific fetch provider and timeout, both retrieved from the global configurations.
 *
 * @param {string} clientId - The client-side ID that identifies the project and environment.
 * @param {string} encodedTargetingParams - The encoded string of targeting parameters that identify the user and other context info.
 * @returns {Promise<Response>} A promise that resolves with the response of the fetch operation.
 *
 * @example
 * // Example of fetching flag evaluations for a client:
 * ldFetch({
 *   clientId: 'your-client-id',
 *   encodedTargetingParams: 'encoded-user-info'
 * })
 * .then(response => response.json())
 * .then(data => console.log(data))
 * .catch(error => console.error('Fetch failed:', error));
 */ function ldFetch(param) {
    var clientId = param.clientId, encodedTargetingParams = param.encodedTargetingParams;
    var _getConfigs = getConfigs(), fetchProvider = _getConfigs.fetchProvider, requestTimeoutMs = _getConfigs.requestTimeoutMs, apiHost = _getConfigs.apiHost;
    // Use fetchWithTimeout with the fetchProvider from configs
    return fetchWithTimeout(fetchProvider, apiHost + "/sdk/eval/" + clientId + "/users/" + encodedTargetingParams, {}, requestTimeoutMs // Using the timeout value from configs
    );
}
/**
 * Fetches feature flags for a specified client and updates the flag cache accordingly.
 * If flags are already being fetched for the client, the existing promise is returned.
 * Otherwise, initiates a new fetch operation. This function also handles updating the cache status,
 * logging errors, and notifying observers based on the fetch result.
 *
 * @param {string} clientId - The unique identifier for the client whose flags are being fetched.
 * @param {Object} log - An optional logging utility which must have an `error` method.
 * @returns {Promise<Object>} A promise that resolves to the fetched flag data, or rejects with an error.
 *
 * @example
 * // Example of fetching flags for a client:
 * fetchFlagsForClient({
 *   clientId: 'client123',
 *   log: console
 * })
 * .then(flags => console.log('Fetched flags:', flags))
 * .catch(error => console.error('Error fetching flags:', error));
 */ function fetchFlagsForClient(param) {
    var clientId = param.clientId, log = param.log;
    var clientCache = flagCache[clientId] || {
        state: {
            subscribers: new Set(),
            status: "new"
        },
        flags: {}
    };
    flagCache[clientId] = clientCache; // newly created objects inserted to cache
    if (clientCache.state.status === "fetching") {
        // Return the existing pending promise if already fetching
        return clientCache.state.promise;
    }
    clientCache.state.targetingParams = getParamsForClient({
        clientId: clientId
    });
    var encodedTargetingParams = encodeTargetingParams(clientCache.state.targetingParams);
    clientCache.state.status = "fetching";
    // Fetch
    clientCache.state.promise = ldFetch({
        clientId: clientId,
        encodedTargetingParams: encodedTargetingParams
    }).then(function(response) {
        if (response.status !== 200) {
            throw new Error("Fetch error: Status " + response.status);
        }
        return response.json().catch(function() {
            throw new Error("Invalid JSON response body");
        });
    }).then(function(data) {
        if (clientCache.state.status === "fetching") {
            clientCache.flags = data;
            clientCache.state.status = "success";
            notifyObservers({
                clientId: clientId
            });
        }
        return data;
    }).catch(function(error) {
        var _log;
        clientCache.state.status = "error";
        clientCache.state.error = error;
        notifyObservers({
            clientId: clientId
        });
        if ((_log = log) == null ? void 0 : _log.error) {
            log.error(error.message);
        } else {
            console.error(error.message);
        }
    });
    return clientCache.state.promise;
}
/**
 * Updates the feature flags for a specified client by re-fetching them if the targeting parameters have changed.
 * This function compares the existing cached parameters with the latest from `getParamsForClient` and,
 * if they differ, triggers a re-fetch using `fetchFlagsForClient`. Idempotent
 *
 * @param {string} clientId - The client identifier used to fetch and update flags.
 * @param {Object} log - An optional logger object that should ideally support an `error` method.
 *
 * @example
 * // Example of updating flags for a client:
 * updateFlagsForClient({
 *   clientId: 'client123',
 *   log: console
 * });
 */ function updateFlagsForClient(param) {
    var clientId = param.clientId, log = param.log;
    var _flagCache_clientId_state, _flagCache_clientId, _flagCache;
    var existingParams = (_flagCache = flagCache) == null ? void 0 : (_flagCache_clientId = _flagCache[clientId]) == null ? void 0 : (_flagCache_clientId_state = _flagCache_clientId.state) == null ? void 0 : _flagCache_clientId_state.targetingParams;
    var newParams = getParamsForClient({
        clientId: clientId
    });
    if (stringifyAndCompare(existingParams, newParams)) {
        return;
    }
    fetchFlagsForClient({
        clientId: clientId,
        log: log
    });
}
/**
 * Idempotent cache insertion mechanism.
 *
 * New cache entries will immediatly trigger a fetch
 *
 * Subsequent cache upserts will return existing cache entry
 *
 * @param  {string} clientId: The clientSideId associated with your project+env in Launch Darkly
 * @returns {Object} Returns existing or created cache entry
 */ function upsert(param) {
    var clientId = param.clientId, _param_targetingParams = param.targetingParams, targetingParams = _param_targetingParams === void 0 ? null : _param_targetingParams, _param_log = param.log, log = _param_log === void 0 ? {} : _param_log;
    if (flagCache[clientId] === undefined) {
        fetchFlagsForClient({
            clientId: clientId,
            targetingParams: targetingParams,
            log: log
        });
    }
    return flagCache[clientId];
}
export { ldFetch, fetchFlagsForClient, updateFlagsForClient, upsert, getParamsForClient, getTargetingParams, setTargetingParams, setClientParams, encodeTargetingParams };
